import toNumber from 'lodash/toNumber'
import parseInt from 'lodash/parseInt'
import type { UseScaleTemplate } from './useScaleTemplate'
import { useScaleTemplate } from './useScaleTemplate'
import qz from 'qz-tray'
import { useError } from '../useError'
import type { QzConsumerProps, QzOption } from './useQz'

export type ScaleOptions = {
  vendorCode: string
  productCode: string
  byteLength: string
  scaleName: string
  qzOption: QzOption
}

export type UseQzScaleProps = QzConsumerProps

export type UseQzScale = {
  onScale: (options?: ScaleOptions) => Promise<ScaleData>
} & UseScaleTemplate

export type ScaleData = {
  status: 'Error' | 'Busy' | 'Stable' | null
  precision: number
  units: 'ounce' | 'pound' | 'gram' | 'kilogram'
  value: number
}

export const useQzScale = ({ useQz }: UseQzScaleProps): UseQzScale => {
  const { scaleName, scaleOptions } = useScaleTemplate()

  const { onError } = useError()
  const { initialize } = useQz()

  const onScale = async (options?: ScaleOptions) => {
    const mergedOptions = { ...scaleOptions, ...options } as ScaleOptions

    const deviceInfo = {
      vendorId: mergedOptions?.vendorCode,
      productId: mergedOptions?.productCode,
    }

    try {
      if (!scaleName || !scaleOptions) {
        throw new Error(`There is no scale template found. Create a new scale template from settings screen`)
      }

      //this will do the initial connection too
      await initialize(mergedOptions.qzOption)

      //we need to claim the device
      await qz.hid.claimDevice(deviceInfo)

      //after claiming and there is no error still we need to read the data
      const result = await qz.hid.readData({ ...deviceInfo, responseSize: mergedOptions.byteLength })

      return rawScaleDataToScaleData(result)
    } catch (err) {
      onError(err)
    } finally {
      if (qz.websocket.isActive()) {
        //we need to release the device
        if (await qz.hid.isClaimed(deviceInfo)) {
          await qz.hid.releaseDevice(deviceInfo)
        }

        await qz.websocket.disconnect()
      }
    }
  }

  return { onScale, scaleName, scaleOptions }
}

/** Attempts to parse scale reading from USB/HID raw output */
export const rawScaleDataToScaleData = (data: string[]): ScaleData => {
  // Filter erroneous data, intentional use of ==
  // eslint-disable-next-line
  if (data.length < 4 || data.slice(2, 8).join('') == '000000000000') {
    return null
  }

  const result: ScaleData = {
    status: null,
    precision: null,
    units: null,
    value: null,
  }

  // Get status
  const status = parseInt(data[1], 16)
  switch (status) {
    case 1: // fault
    case 5: // underweight
    case 6: // overweight
    case 7: // calibrate
    case 8: // re-zero
      result.status = 'Error'
      break
    case 3: // busy
      result.status = 'Busy'
      break
    case 2: // stable at zero
    case 4: // stable non-zero
    default:
      result.status = 'Stable'
  }

  // Get precision
  let precision = parseInt(data[3], 16)
  precision = precision ^ -256 //unsigned to signed

  // xor on 0 causes issues intentional use of ==
  // eslint-disable-next-line
  if (precision == -256) {
    precision = 0
  }

  //assign the precision value
  result.precision = precision

  // Get units
  const units = parseInt(data[2], 16)
  switch (units) {
    case 2:
      result.units = 'gram'
      break
    case 3:
      result.units = 'kilogram'
      break
    case 11:
      result.units = 'ounce'
      break
    case 12:
    default:
      result.units = 'pound'
  }

  // Get weight
  data.splice(0, 4)
  data.reverse()
  let weight = parseInt(data.join(''), 16)

  weight *= Math.pow(10, precision)
  result.value = toNumber(weight.toFixed(Math.abs(precision)))

  return result
}
